import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiX } from 'react-icons/fi';
import forecastService from '../../services/forecastService';
import projectService from '../../services/projectService';
import './Forecasts.css';

const ForecastEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    project_id: '',
    forecast_type: 'Budget',
    period_type: 'Monthly',
    forecast_date: '',
    title: '',
    description: '',
    baseline_value: '',
    forecasted_value: '',
    actual_value: '',
    confidence_level: 'Medium',
    status: 'Draft',
    methodology: '',
    assumptions: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadProjects();
    loadForecast();
  }, [id]);

  const loadProjects = async () => {
    try {
      const response = await projectService.getAllProjects({ limit: 1000 });
      setProjects(response.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const loadForecast = async () => {
    try {
      setLoading(true);
      const response = await forecastService.getById(id);
      const forecast = response.data;
      
      setFormData({
        project_id: forecast.project_id || '',
        forecast_type: forecast.forecast_type || 'Budget',
        period_type: forecast.period_type || 'Monthly',
        forecast_date: forecast.forecast_date ? forecast.forecast_date.split('T')[0] : '',
        title: forecast.title || '',
        description: forecast.description || '',
        baseline_value: forecast.baseline_value || '',
        forecasted_value: forecast.forecasted_value || '',
        actual_value: forecast.actual_value || '',
        confidence_level: forecast.confidence_level || 'Medium',
        status: forecast.status || 'Draft',
        methodology: forecast.methodology || '',
        assumptions: forecast.assumptions || '',
        notes: forecast.notes || ''
      });
    } catch (error) {
      console.error('Error loading forecast:', error);
      toast.error('Failed to load forecast');
      navigate('/app/forecasts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.project_id) newErrors.project_id = 'Project is required';
    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.forecast_date) newErrors.forecast_date = 'Forecast date is required';
    if (!formData.forecasted_value) newErrors.forecasted_value = 'Forecasted value is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      
      const submitData = {
        ...formData,
        baseline_value: formData.baseline_value ? parseFloat(formData.baseline_value) : null,
        forecasted_value: parseFloat(formData.forecasted_value),
        actual_value: formData.actual_value ? parseFloat(formData.actual_value) : null
      };

      await forecastService.update(id, submitData);
      toast.success('Forecast updated successfully');
      navigate('/app/forecasts');
    } catch (error) {
      console.error('Error updating forecast:', error);
      toast.error(error.response?.data?.message || 'Failed to update forecast');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="forecasts-page">
        <div className="forecasts-loading">Loading forecast...</div>
      </div>
    );
  }

  return (
    <div className="forecasts-page">
      <div className="forecasts-header">
        <div className="forecasts-header-left">
          <h1 className="forecasts-title">Edit Forecast</h1>
          <p className="forecasts-subtitle">Update forecast information</p>
        </div>
      </div>

      <div className="forecast-form-container">
        <form onSubmit={handleSubmit} className="forecast-form">
          {/* Basic Information */}
          <div className="form-section">
            <h3 className="form-section-title">Basic Information</h3>
            
            <div className="form-row custom-row">
              <div className="form-group">
                <label className="form-label">
                  Project <span className="required">*</span>
                </label>
                <select
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleChange}
                  className={`form-input ${errors.project_id ? 'error' : ''}`}
                  required
                >
                  <option value="">Select Project</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {errors.project_id && <span className="error-message">{errors.project_id}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`form-input ${errors.title ? 'error' : ''}`}
                  placeholder="Enter forecast title"
                  required
                />
                {errors.title && <span className="error-message">{errors.title}</span>}
              </div>
            </div>

            <div className="form-row custom-row">
              <div className="form-group">
                <label className="form-label">
                  Forecast Type <span className="required">*</span>
                </label>
                <select
                  name="forecast_type"
                  value={formData.forecast_type}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="Budget">Budget</option>
                  <option value="Timeline">Timeline</option>
                  <option value="Resource">Resource</option>
                  <option value="Revenue">Revenue</option>
                  <option value="Risk">Risk</option>
                  <option value="Quality">Quality</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Period Type <span className="required">*</span>
                </label>
                <select
                  name="period_type"
                  value={formData.period_type}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Forecast Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  name="forecast_date"
                  value={formData.forecast_date}
                  onChange={handleChange}
                  className={`form-input ${errors.forecast_date ? 'error' : ''}`}
                  required
                />
                {errors.forecast_date && <span className="error-message">{errors.forecast_date}</span>}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
                placeholder="Enter forecast description"
              />
            </div>
          </div>

          {/* Values */}
          <div className="form-section">
            <h3 className="form-section-title">Values</h3>
            
            <div className="form-row custom-row">
              <div className="form-group">
                <label className="form-label">Baseline Value</label>
                <input
                  type="number"
                  name="baseline_value"
                  value={formData.baseline_value}
                  onChange={handleChange}
                  className="form-input"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Forecasted Value <span className="required">*</span>
                </label>
                <input
                  type="number"
                  name="forecasted_value"
                  value={formData.forecasted_value}
                  onChange={handleChange}
                  className={`form-input ${errors.forecasted_value ? 'error' : ''}`}
                  step="0.01"
                  placeholder="0.00"
                  required
                />
                {errors.forecasted_value && <span className="error-message">{errors.forecasted_value}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Actual Value</label>
                <input
                  type="number"
                  name="actual_value"
                  value={formData.actual_value}
                  onChange={handleChange}
                  className="form-input"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Status & Confidence */}
          <div className="form-section">
            <h3 className="form-section-title">Status & Confidence</h3>
            
            <div className="form-row custom-row">
              <div className="form-group">
                <label className="form-label">Confidence Level</label>
                <select
                  name="confidence_level"
                  value={formData.confidence_level}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Very High">Very High</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="Draft">Draft</option>
                  <option value="Active">Active</option>
                  <option value="Completed">Completed</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="form-section">
            <h3 className="form-section-title">Additional Information</h3>
            
            <div className="form-group">
              <label className="form-label">Methodology</label>
              <textarea
                name="methodology"
                value={formData.methodology}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
                placeholder="Describe the forecasting methodology used"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Assumptions</label>
              <textarea
                name="assumptions"
                value={formData.assumptions}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
                placeholder="List key assumptions made in this forecast"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
                placeholder="Additional notes"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/app/forecasts')}
              className="btn-secondary"
              disabled={saving}
            >
              <FiX />
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              <FiSave />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForecastEdit;

